import { Component, Input, EventEmitter, Output, forwardRef, AfterContentInit } from '@angular/core';
import { ToasterService } from 'src/app/_services/toaster.service';
import { AzureBlobSASViewModel, StringViewModel, VideoViewModel } from 'src/app/_models/generatedModels';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ISasToken } from 'src/app/_services/azureStorage';
import { BlobStorageService } from 'src/app/_services/blob-storage.service';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ExerciseService, VideoService } from 'src/app/_services/generatedServices';
interface IUploadProgress {
  filename: string;
  progress: number;
}

@Component({
  selector: 'video-upload',
  templateUrl: 'video-upload.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => VideoUploadComponent),
      multi: true,
    },
  ],
})
export class VideoUploadComponent implements ControlValueAccessor, AfterContentInit {
  initialized = false;
  selectedValue: number;

  fileName: string;
  fileNameDisplay: string;
  currentFile: any;
  uploading = false;
  percentComplete: number = -1;
  uploadProgress$: Observable<IUploadProgress[]>;
  public uploadData: AzureBlobSASViewModel;

  @Input()
  label: string = "Upload Video";

  @Input()
  showLabel: boolean = true;

  @Input()
  allowAnonymous: boolean = false;

  @Input()
  validateCameraUse: boolean = false;

  @Output()
  valueChange = new EventEmitter<number>();

  propagateChange = (_: any) => {};
  propagateTouch = () => {};

  writeValue(value: number): void {
    this.selectedValue = value;
  }

  registerOnChange(fn: any): void {
    this.propagateChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.propagateTouch = fn;
  }

  onChange(value: any) {
    this.writeValue(value);
    this.propagateChange(this.selectedValue);
    this.propagateTouch();
    this.valueChange.emit(value);
  }

  constructor(private toastr: ToasterService, private blobStorage: BlobStorageService, private videoService: VideoService) {}

  ngAfterContentInit(): void {
    if (this.selectedValue) {
      this.videoService.getVideoById(this.selectedValue).subscribe(result => {
        this.fileNameDisplay = result.fileNameDisplay;
        this.initialized = true;
      });
    } else {
      this.initialized = true;
    }
  }

  onFileChange(event) {
    this.currentFile = event.target.files[0];

    if (!this.currentFile) return;

    if (this.validateCameraUse) {
      const now = Date.now();
      const diffSec = (now - this.currentFile.lastModified) / 1000;
      if (diffSec < 15) {
        this.toastr.okDialog('Please do not record videos directly within the submission page as this tends to significantly reduce quality. Record the video from your camera app first, then upload from your photo library for best results.', 'Video quality');
      }
      else {
        this.toastr.okModal(
          `<b>Please make sure your video follows these guidelines:</b>
           <ul>
             <li>Hold the camera steady (don’t pan to follow runner).</li>
             <li>Record at 60 fps for best results.</li>
             <li>Orthogonal side/back view, camera not at an angle.</li>
             <li>Trim the video to show running in only one direction at steady speed.</li>
             <li>Full body visible.</li>
             <li>Run on flat, solid ground or on a treadmill.</li>
             <li>Show at least 6 running steps in the video.</li>
           </ul>`,
          'Video Quality Check'
        ).subscribe();
      }
    }

    const maxSizeInBytes = 100 * 1024 * 1024; // 100MB
    if (this.currentFile.size > maxSizeInBytes) {
      this.toastr.okDialog('The video size exceeds 100 MB. Please trim the file and re-upload it.', 'The video file is too big');
      (event.target as HTMLInputElement).value = ''; // Reset the file input
      return;
    }

    this.percentComplete = 1;

    let model = new StringViewModel();
    model.value = this.currentFile.name;

    if (this.allowAnonymous) {
      this.videoService.getPublicSASForVideoContainer(model).subscribe((result) =>
      {
        this.uploadData = result;

        this.uploadFile(this.currentFile).subscribe(
          (progress) =>
          {
            if (progress.progress === 0) {
              this.percentComplete = 1;
            } else {
              this.percentComplete = progress.progress;
            }
          },
          (error) => { },
          () =>
          {
            this.successEventHandler(null);
          }
        );
      });
    } else {
      this.videoService.getSASForVideoContainer(model).subscribe((result) =>
      {
        this.uploadData = result;

        this.uploadFile(this.currentFile).subscribe(
          (progress) =>
          {
            if (progress.progress === 0) {
              this.percentComplete = 1;
            } else {
              this.percentComplete = progress.progress;
            }
          },
          (error) => { },
          () =>
          {
            this.successEventHandler(null);
          }
        );
      });
    }
  }

  uploadFile(file: File): Observable<IUploadProgress> {
    const accessToken: ISasToken = {
      container: this.uploadData.containerName,
      filename: this.uploadData.fileName,
      storageAccessToken: this.uploadData.sasToken,
      storageUri: this.uploadData.storageUri,
    };

    return this.blobStorage.uploadToBlobStorage(accessToken, file).pipe(map((progress) => this.mapProgress(file, progress)));
  }

  private mapProgress(file: File, progress: number): IUploadProgress {
    return {
      filename: file.name,
      progress: progress,
    };
  }

  successEventHandler(e) {
    this.fileName = this.uploadData.fileName;
    this.fileNameDisplay = this.currentFile.name;

    const model: VideoViewModel = {
      fileNameDisplay: this.fileNameDisplay,
      fileName: this.fileName
    }
    if (this.allowAnonymous) {
      this.videoService.addPublicVideo(model).subscribe(result =>
      {
        this.onChange(result.id);
        this.percentComplete = -1;
      });
    } else {
      this.videoService.addVideo(model).subscribe(result =>
      {
        this.onChange(result.id);
        this.percentComplete = -1;
      });
    }
  }
}
